import { useEffect, useState } from 'react';
import {
  EVENTS_CHANGED_EVENT,
  EVENTS_LOADED_EVENT,
  eventsLoaded,
  loadEvents,
  refreshEventsFromApi,
  type StoredEvent,
} from '../services/EventsService';

/**
 * The project's active events plus whether the first load has landed. A failed
 * load leaves `loaded` false, so a screen can tell "no events" from "not known yet".
 */
export function useStoredEventsState(): { events: StoredEvent[]; loaded: boolean } {
  const [events, setEvents] = useState<StoredEvent[]>(() => loadEvents());
  const [loaded, setLoaded] = useState<boolean>(() => eventsLoaded());

  useEffect(() => {
    const sync = () => {
      setEvents(loadEvents());
      setLoaded(eventsLoaded());
    };
    window.addEventListener(EVENTS_LOADED_EVENT, sync);
    window.addEventListener(EVENTS_CHANGED_EVENT, sync);
    if (!eventsLoaded()) void refreshEventsFromApi();
    sync();
    return () => {
      window.removeEventListener(EVENTS_LOADED_EVENT, sync);
      window.removeEventListener(EVENTS_CHANGED_EVENT, sync);
    };
  }, []);

  return { events, loaded };
}

/**
 * The project's active events, kept in step with the cache. A picker that used
 * to read `loadEvents()` once on mount saw an empty list whenever it mounted
 * before the first load finished; this re-reads when the load lands, and starts
 * the load itself when nothing has asked for it yet.
 */
export function useStoredEvents(): StoredEvent[] {
  return useStoredEventsState().events;
}
